import React, { useState } from 'react';

import { UseSDKMethodFormReturn } from '@/features/method-tester/types/formTypes';
import { Button } from '@asphalt-react/button';
import { Cross, Edit, Tick } from '@asphalt-react/iconpack';
import { Textfield } from '@asphalt-react/textfield';
import { Text } from '@asphalt-react/typography';

interface ParameterInputProps {
  formData: UseSDKMethodFormReturn;
}

const ParameterInput: React.FC<ParameterInputProps> = ({ formData }) => {
  const {
    formState,
    updateParamKey,
    updateParamValue,
    addParamRow,
    removeParam,
  } = formData;

  const {
    paramKeys,
    data: { params },
  } = formState;
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [originalValues, setOriginalValues] = useState<{
    key: string;
    value: string;
  } | null>(null);

  const handleKeyInputChange = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      updateParamKey(idx, event.target.value);
    };
  };

  const handleValueInputChange = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      updateParamValue(idx, event.target.value);
    };
  };

  const handleEditClick = (idx: number) => {
    const key = paramKeys[idx];
    const value = params[key] || '';
    setOriginalValues({ key, value });
    setEditingIndex(idx);
  };

  const handleSaveEdit = () => {
    setEditingIndex(null);
    setOriginalValues(null);
  };

  const handleCancelEdit = () => {
    if (editingIndex !== null && originalValues) {
      updateParamKey(editingIndex, originalValues.key);
      updateParamValue(editingIndex, originalValues.value);
    }
    setEditingIndex(null);
    setOriginalValues(null);
  };

  const canAddParameter = (idx: number) => {
    const key = paramKeys[idx];
    const value = params[key];
    return key?.trim() && value?.trim();
  };

  const handleRemoveParam = (idx: number) => {
    removeParam(idx);
  };

  return (
    <>
      <Text size="s">Parameters:</Text>

      {paramKeys.map((key, idx) => {
        const isLastItem = idx === paramKeys.length - 1;
        const isEditing = editingIndex === idx;
        const paramValue = params[key] || '';
        const canAdd = canAddParameter(idx);
        return (
          <div key={`param-${idx}`} className="flex items-center gap-2 mb-2">
            {isLastItem || isEditing ? (
              <ParameterInputRow
                keyValue={key}
                paramValue={paramValue}
                onKeyChange={handleKeyInputChange(idx)}
                onValueChange={handleValueInputChange(idx)}
                placeholder={{ key: 'Key', value: 'Value' }}
              />
            ) : (
              <ParameterDisplayRow keyValue={key} paramValue={paramValue} />
            )}

            {/* Action buttons */}
            <div className="flex-shrink-0 flex gap-1">
              {isLastItem ? (
                <Button onClick={addParamRow} disabled={!canAdd} size="xs">
                  Add
                </Button>
              ) : isEditing ? (
                <>
                  <Button onClick={handleSaveEdit} size="xs" icon tertiary>
                    <Tick />
                  </Button>
                  <Button
                    onClick={handleCancelEdit}
                    size="xs"
                    icon
                    system
                    tertiary
                  >
                    <Cross />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => handleEditClick(idx)}
                    size="xs"
                    icon
                    tertiary
                  >
                    <Edit />
                  </Button>
                  {paramKeys.length > 1 && (
                    <Button
                      onClick={() => handleRemoveParam(idx)}
                      size="xs"
                      icon
                      system
                      tertiary
                    >
                      <Cross />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

/**
 * Input row component for entering/editing parameters
 */
interface ParameterInputRowProps {
  keyValue: string;
  paramValue: string;
  onKeyChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: { key: string; value: string };
}

const ParameterInputRow: React.FC<ParameterInputRowProps> = ({
  keyValue,
  paramValue,
  onKeyChange,
  onValueChange,
  placeholder,
}) => (
  <>
    <Textfield
      type="text"
      value={keyValue}
      onChange={onKeyChange}
      placeholder={placeholder.key}
      size="xs"
      stretch
      aria-label="Parameter key"
    />
    <Textfield
      type="text"
      value={paramValue}
      onChange={onValueChange}
      placeholder={placeholder.value}
      size="xs"
      stretch
      aria-label="Parameter value"
    />
  </>
);

/**
 * Display row component for showing parameters in read-only mode
 */
interface ParameterDisplayRowProps {
  keyValue: string;
  paramValue: string;
}

const PARAMETER_BASE_CLASSNAME =
  'flex-1 min-w-0 px-2 py-1 text-gray-700 text-sm overflow-hidden text-ellipsis whitespace-nowrap rounded';
const PARAMETER_KEY_CLASSNAME = `${PARAMETER_BASE_CLASSNAME} bg-gray-50 font-medium`;
const PARAMETER_VALUE_CLASSNAME = `${PARAMETER_BASE_CLASSNAME} bg-gray-100`;

const ParameterDisplayRow: React.FC<ParameterDisplayRowProps> = ({
  keyValue,
  paramValue,
}) => (
  <>
    <div className={PARAMETER_KEY_CLASSNAME}>{keyValue}</div>
    <span className="text-gray-400">:</span>
    <div className={PARAMETER_VALUE_CLASSNAME}>{paramValue || '(empty)'}</div>
  </>
);

export default ParameterInput;
